/**
 * WARNING: This module must not indirectly import WebContainer.
 */

import { LARGE_REQUEST_TOKEN_COUNT } from '~/lib/constants';
import type { Message } from '~/types/chat';
import { invariant } from '~/utils/invariant';
import { wait } from '~/utils/promises';

export type ContextSizeEvent = {
  messages: Message[];
} & (OnFinishEvent | LoadHistoryEvent | CheckEvent);

interface OnFinishEvent {
  kind: 'on-finish';

  // this count should include all type of input tokens
  count: number;
}

interface LoadHistoryEvent {
  kind: 'load-history';
}

interface CheckEvent {
  kind: 'check';
}

/**
 * Tracks and estimates context size using multiple sources:
 *
 *   1. Synchronous local token estimator
 *   2. Remote '/api/context-size' endpoint
 *   3. Historical token counts from previous '/api/chat' responses
 *
 * The remote endpoint (2) is only used when there's risk of an expensive
 * API call that the local estimator can't accurately predict, such as
 * messages containing non-image attachments.
 *
 * In all other cases, the local estimator (1) is preferred for performance.
 */
export class ContextSizeTracker {
  #serverEstimate: number | undefined;
  #messagesIncludedInServerEstimate: string[] | undefined;

  #pendingRefresh: Promise<unknown> = Promise.resolve();
  #refreshId: number = 0;

  /**
   * Updates token count estimate and returns whether
   * the context size exceeds the maximum limit.
   */
  async refresh(event: ContextSizeEvent): Promise<boolean> {
    const { chatStore } = await import('~/lib/stores/chat');

    if (chatStore.promptMode.get() !== 'build') {
      return false;
    }

    const { estimateTokens } = await import('~/utils/token-count-estimator');

    /**
     * Always wait for the previous refresh to complete, but limit the wait time to 400ms
     * to prevent blocking the UI for too long when remote calls are slow.
     */
    await Promise.race([this.#pendingRefresh, wait(400)]);

    /**
     * We use a "refreshId" to know when a remote check is done whether
     * if it contains up to date information or not. If it does not we discard
     * its result and don't update the state.
     */
    this.#refreshId += 1;

    const { messages } = event;

    switch (event.kind) {
      /**
       * For 'on-finish' we just got a response from the server
       * with the exact token count of the current chat. At this
       * point the server estimate is exact.
       */
      case 'on-finish': {
        this.#serverEstimate = event.count;
        this.#messagesIncludedInServerEstimate = messages.map((message) => message.id);

        return this.#serverEstimate > LARGE_REQUEST_TOKEN_COUNT;
      }

      /**
       * When we just loaded the chat history we've got time so
       * we ask the backend for an estimation.
       */
      case 'load-history': {
        const promise = this.#countTokens(messages);

        this.#pendingRefresh = promise;

        return promise;
      }

      /**
       * When a check is made, we want to be as fast as possible but we
       * still have a slower path in case the request contains PDF
       * attachments in the last message.
       */
      case 'check':
      default: {
        const lastMessage = messages.at(-1);

        invariant(lastMessage);

        // if the last message has non image attachements we do a backend check immediately
        if (hasNonImageAttachments(lastMessage)) {
          const promise = this.#countTokens(messages);

          this.#pendingRefresh = promise;

          return promise;
        }

        /**
         * We compare the previous cached values with messages in the queue to know where to
         * start the estimation to have a better estimate.
         *
         * Note that if any id changed we discard the cached values.
         */
        let startPosition = 0;

        const prevMessageIds = this.#messagesIncludedInServerEstimate ?? [];

        for (let i = 0; i < prevMessageIds.length; ++i) {
          if (messages[i].id !== prevMessageIds[i]) {
            startPosition = 0;
            break;
          }

          startPosition = i;
        }

        const serverEstimate = startPosition === 0 ? 0 : this.#serverEstimate ?? 0;
        const count = estimateTokens(messages.slice(startPosition)) + serverEstimate;

        return count > LARGE_REQUEST_TOKEN_COUNT;
      }
    }
  }

  async #countTokens(messages: Message[]) {
    const id = this.#refreshId;

    const { countTokens } = await import('~/lib/.client/api');

    const serverEstimate = await countTokens(messages);

    if (id === this.#refreshId) {
      this.#serverEstimate = serverEstimate;
      this.#messagesIncludedInServerEstimate = messages.map((message) => message.id);
    } else {
      await this.#pendingRefresh;
    }

    return !!this.#serverEstimate && this.#serverEstimate > LARGE_REQUEST_TOKEN_COUNT;
  }
}

function hasNonImageAttachments(message: Message) {
  if (!message.experimental_attachments || message.experimental_attachments.length === 0) {
    return false;
  }

  return message.experimental_attachments.some(({ contentType }) => !contentType?.startsWith('image/'));
}
